import React, { useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import '../assets/AllTabsCommon.css';
import '../assets/DeviceProfile.css';
import '../assets/Reports.css';
import TimeSeriesChart from '../reports/TimeSeriesChart';
import HistogramChart from '../reports/HistogramChart';
import DebugStrategies from '../reports/DebugStrategies';
import ActiveDebugSessions from '../reports/ActiveDebugSessions';
import useDeviceActiveSessions from '../../hooks/useDeviceActiveSessions';
import {
  deviceSummary,
  aiReasoning,
  timelineFilters,
  subModuleAnomalies,
  denseTelemetryChips,
} from './deviceProfileMockData';
import {
  fetchDeviceProfileCrashAnalysis,
  fetchDeviceProfile,
  fetchActivitySummary,
  fetchHealthScore,
  fetchDeviceProfileUnifiedTimeline,
  fetchDeviceProfileDenseHealth,
  fetchDeviceProfileSignalHistograms,
  fetchArtifactDownloadUrl,
  fetchDeviceProfileTimelineSummary,
  fetchAnalyzedLogs,
} from '../api/restapi';
import {
  collectLogSourceIds,
  describeAnalysis,
  indexAnalyzedLogs,
  isLogEvent,
  logArtifactId,
} from '../utils/analyzedLogs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

const ACTIVITY_FIELD_META = {
  'Crowdsourced Telemetry Data Points': { icon: '📡', tone: 'blue' },
  'Anomalies (Device Detected)': { icon: '⚠️', tone: 'yellow' },
  'Anomalies (Cloud Detected)': { icon: '⚠️', tone: 'yellow' },
  'RD Sessions': { icon: '🔧', tone: 'blue' },
  'Crash Events': { icon: '💥', tone: 'red' },
  'ODL Uploads': { icon: '📋', tone: 'green' },
  'RT Telemetry Data Points': { icon: '📶', tone: 'blue' },
  'RT Sessions': { icon: '🔁', tone: 'blue' },
};

const PROFILE_GROUPS = [
  { key: 'IDENTITY', title: '🪪 Identity' },
  { key: 'Build Versions', title: '💾 Build Versions' },
  { key: 'Device State', title: '📊 Device State' },
  { key: 'Gateway Cloud Connect Client', title: '🔗 Gateway Cloud Connect Client' },
];

// Derive the same {muted,tag} presentation the old canned rows hardcoded,
// from the field name + value alone.
function styleForField(fieldName, value) {
  if (value === 'Unavailable' || value === 'Coming Soon') return { muted: true };
  if (fieldName === 'Data Stall') return { tag: value === 'No Data Stalls Detected' ? 'ok' : 'changed' };
  if (fieldName === 'Memory') return { tag: value === 'Normal' ? 'ok' : 'changed' };
  if (fieldName === 'Thermal') return { tag: value === 'Normal' ? 'ok' : 'changed' };
  return {};
}

// Last Reboot / Data Stall carry their instant as ISO UTC in params.timestamp; the
// server-formatted `value` text is UTC, so re-render that instant in local time.
function localizedProfileValue(row) {
  const ts = row.params?.timestamp;
  if (!ts) return row.value;
  if (row.fieldName === 'Last Reboot') return isoToDateTime(ts, { withSeconds: false });
  if (row.fieldName === 'Data Stall') return `Stalled ${isoToDateTime(ts, { withSeconds: false })}`;
  return row.value;
}

// Health Score API response is a nested { healthScores: [{ domains: [{ subdomains:
// [{ metrics: [{ penaltyLabel }] }] }] }] } tree. Count every metric across all
// windows/domains/subdomains whose penaltyLabel is "bad" — this is the cloud-detected
// anomaly count. Returns null (caller falls back to "Unavailable") if malformed.
function countBadPenalties(healthScoreResponse) {
  const windows = healthScoreResponse?.healthScores;
  if (!Array.isArray(windows)) return null;
  let count = 0;
  for (const w of windows) {
    for (const domain of w.domains || []) {
      for (const sub of domain.subdomains || []) {
        for (const m of sub.metrics || []) {
          if (m.penaltyLabel === 'bad') count += 1;
        }
      }
    }
  }
  return count;
}

// Thermal status from the most recent (last) health-score window: "Elevated" if
// the THERMAL subdomain's penaltyLabel is "bad", else "Normal". Returns null if
// the THERMAL subdomain isn't present/malformed.
function resolveThermalStatus(healthScoreResponse) {
  const windows = healthScoreResponse?.healthScores;
  if (!Array.isArray(windows) || windows.length === 0) return null;
  const latest = windows[windows.length - 1];
  for (const domain of latest.domains || []) {
    for (const sub of domain.subdomains || []) {
      if (sub.subDomainLabel === 'THERMAL') {
        const bad = (sub.metrics || []).some((m) => m.penaltyLabel === 'bad');
        return bad ? 'Elevated' : 'Normal';
      }
    }
  }
  return null;
}

// Format an ISO timestamp into a clock label (e.g. "10:02:47 AM").
// Backend timestamps are UTC; rendered in the browser's local time zone.
function isoToClock(iso, { withSeconds = true } = {}) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  });
}

// Format an ISO timestamp into a short date label (e.g. "Jul 10, 2025").
// Backend timestamps are UTC; rendered in the browser's local time zone.
function isoToDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

// Combined date + clock label (e.g. "Jul 10, 2025, 10:02:47 AM") -- the single
// canonical timestamp format for this tab; every rendered timestamp should go
// through this instead of printing raw ISO strings or reformatting inline.
function isoToDateTime(iso, options) {
  const date = isoToDate(iso);
  const clock = isoToClock(iso, options);
  if (!date || !clock) return date || clock;
  return `${date}, ${clock}`;
}

// Format an instant as browser-local ISO-8601 with its UTC offset, e.g.
// "2025-07-10T15:30:00+05:30" — used for the AI-summary payload so the LLM
// reports times in the operator's local zone. Returns the input unchanged if unparseable.
function toLocalIsoWithOffset(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const p = (n) => String(Math.abs(n)).padStart(2, '0');
  const off = -d.getTimezoneOffset();
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` +
    `${off >= 0 ? '+' : '-'}${p(Math.trunc(off / 60))}:${p(off % 60)}`
  );
}

// Signed minute offset of an event timestamp relative to the reference Date.
function offsetMinutes(iso, referenceDate) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || !referenceDate || Number.isNaN(referenceDate.getTime())) return 0;
  return Math.round((d.getTime() - referenceDate.getTime()) / 60000);
}

function formatBytes(bytes) {
  if (bytes == null) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// Category → the { cat, dot } pair the timeline row renderer expects. `cat` also drives filter pills.
function categoryFor(eventType, isCritical) {
  switch (eventType) {
    case 'CRASH':
      return { cat: 'crash', dot: 'crash' };
    case 'ODL':
      return { cat: 'odl', dot: 'odl' };
    case 'MQTT_CLIENT_REGISTRATION':
      return { cat: 'mqtt', dot: 'reg' };
    case 'HEALTH':
      return { cat: 'health', dot: 'health' };
    case 'RT_TELEMETRY':
      return { cat: 'rt', dot: isCritical ? 'anomaly' : 'rt' };
    case 'AD_LOG':
      return { cat: 'adlog', dot: 'adlog' };
    case 'AD_TELEMETRY':
      return { cat: 'adtelemetry', dot: 'adtelemetry' };
    case 'CONNECTION':
      return { cat: 'connection', dot: 'connection' };
    case 'TELEMETRY':
    default:
      return { cat: 'telemetry', dot: isCritical ? 'anomaly' : 'telemetry' };
  }
}

// Small badge for the event's category (mirrors the mock badge styles).
function categoryBadge(eventType) {
  switch (eventType) {
    case 'CRASH':
      return { t: 'Crash', c: 'crash' };
    case 'ODL':
      return { t: 'ODL', c: 'odl' };
    case 'MQTT_CLIENT_REGISTRATION':
      return { t: 'MQTT Client Registration', c: 'mqtt' };
    case 'HEALTH':
      return { t: 'Health', c: 'health' };
    case 'RT_TELEMETRY':
      return { t: 'RT Telemetry', c: 'rt' };
    case 'AD_LOG':
      return { t: 'AD Log', c: 'adlog' };
    case 'AD_TELEMETRY':
      return { t: 'AD Telemetry', c: 'adtelemetry' };
    case 'CONNECTION':
      return { t: 'MQTT Connection', c: 'connection' };
    case 'TELEMETRY':
    default:
      return { t: 'Telemetry', c: 'telemetry' };
  }
}

// Display labels for TELEMETRY sub-types (eventSubType) in the timeline's Event Type sub-filter.
const TELEMETRY_SUB_LABELS = {
  THROUGHPUT: 'UL/DL Throughput',
  MODEM_SSR: 'Modem SSR',
  PDN: 'PDN',
  NAS_REGISTRATION: 'NAS Registration',
  DATA_STALL: 'Data Stall',
  DATA_STALL_RECOVERY_ATTEMPT: 'Data Stall Recovery',
  BOOT_TIME: 'Boot',
};

// Sub-type key + label an event contributes to its category's Event Type sub-filter. Telemetry,
// RT, AD telemetry and connection events key on eventSubType (so e.g. PDN Up/Down group together);
// every other category keys on the event title (e.g. Device/AP/Modem Restart, Minidump/Microdump).
function subFilterFor(event) {
  const title = event.eventTitle || 'Other';
  switch (event.eventType) {
    case 'TELEMETRY':
      return { sub: event.eventSubType || title, subLabel: TELEMETRY_SUB_LABELS[event.eventSubType] || title };
    case 'AD_TELEMETRY':
      return { sub: event.eventSubType || title, subLabel: title.replace(/^Anomaly Detected: /, '') };
    case 'RT_TELEMETRY':
    case 'CONNECTION':
      return { sub: event.eventSubType || title, subLabel: title };
    default:
      return { sub: title, subLabel: title };
  }
}

// Push a "key: value" detail pair (value rendered as a <code> span) when the value is present.
function pushDetail(segments, label, value) {
  if (value === null || value === undefined || value === '') return;
  if (segments.length > 0) segments.push({ text: '  ·  ' });
  segments.push({ text: `${label}: ` });
  segments.push({ code: String(value) });
}

// Convert bits-per-second to Mbps for display (1 decimal). Null-safe.
function bpsToMbps(bps) {
  if (bps === null || bps === undefined) return null;
  return (Number(bps) / 1_000_000).toFixed(1);
}

// Count entries in the RT neighbor-cell payload, stored as a JSON string. Returns
// null if absent/unparseable so the detail line is omitted.
function neighborCellCount(neighborCellsJson) {
  if (!neighborCellsJson) return null;
  try {
    const parsed = JSON.parse(neighborCellsJson);
    if (Array.isArray(parsed)) return parsed.length;
    if (Array.isArray(parsed?.neighborCells)) return parsed.neighborCells.length;
    return null;
  } catch {
    return null;
  }
}

// Build the detail-line segments for a live timeline event from its typed `data` payload.
export function buildDetailSegments(event) {
  const segments = [];
  const data = event.data || {};
  switch (event.eventType) {
    case 'CRASH': {
      pushDetail(segments, 'Dump Type', data.dumpType);
      pushDetail(segments, 'Filename', data.filename);
      pushDetail(segments, 'Crash Area', data.problem?.crashArea);
      pushDetail(segments, 'Crash Error', data.problem?.crashError);
      break;
    }
    case 'HEALTH': {
      pushDetail(segments, 'Health Score', data.scorePercentage != null ? `${data.scorePercentage}%` : null);
      break;
    }
    case 'RT_TELEMETRY': {
      const payload = data.payload || {};
      const header = data.commonHeader || {};
      switch (event.eventSubType) {
        case 'signal-strength-rsrp':
        case 'signal-strength-rsrq':
        case 'signal-strength-sinr':
          pushDetail(segments, 'LTE', payload.lteValue);
          pushDetail(segments, '5G NR', payload.nr5gValue);
          break;
        case 'serving-cell-metrics':
          pushDetail(segments, 'RAT', header.wwanTech);
          pushDetail(segments, 'PCI', header.servingPci);
          pushDetail(segments, 'EARFCN', header.earfcn);
          pushDetail(segments, 'ARFCN', header.arfcn);
          pushDetail(segments, 'MCC/MNC', header.mcc != null && header.mnc != null ? `${header.mcc}/${header.mnc}` : null);
          pushDetail(segments, 'LTE', payload.lteValue);
          pushDetail(segments, '5G NR', payload.nr5gValue);
          break;
        case 'neighbor-cell-metrics':
          pushDetail(segments, 'Neighbor Cells', neighborCellCount(payload.neighborCellsJson));
          break;
        case 'throughput-performance':
          pushDetail(segments, 'UL Peak (Mbps)', bpsToMbps(payload.ulPeakBps));
          pushDetail(segments, 'DL Peak (Mbps)', bpsToMbps(payload.dlPeakBps));
          pushDetail(segments, 'RTT (ms)', payload.rttMs);
          pushDetail(segments, 'Jitter (ms)', payload.jitterMs);
          pushDetail(segments, 'Packet Loss (%)', payload.packetLossPct);
          break;
        default:
          break;
      }
      pushDetail(segments, 'Error', header.errorString);
      break;
    }
    case 'ODL':
    case 'AD_LOG': {
      pushDetail(segments, 'Filename', data.filename);
      pushDetail(segments, 'Files Aggregated', data.filesMergedCount);
      pushDetail(segments, 'Collection Start', isoToClock(data.collectionStartTimestamp));
      pushDetail(segments, 'Collection End', isoToClock(data.collectionEndTimestamp));
      break;
    }
    case 'AD_TELEMETRY': {
      pushDetail(segments, 'Anomaly ID', data.anomalyId);
      pushDetail(segments, 'Source', data.source);
      pushDetail(segments, 'Logging', data.loggingState);
      break;
    }
    case 'CONNECTION': {
      pushDetail(segments, 'Reason', data.reason);
      pushDetail(segments, 'Reason Code', data.reasonCode);
      pushDetail(segments, 'Source', data.source);
      break;
    }
    case 'MQTT_CLIENT_REGISTRATION': {
      if (event.eventSubType === 'RESTART') {
        pushDetail(segments, 'Restart Type', data.restartType);
        pushDetail(segments, 'Uptime At Registration (s)', data.uptimeAtRegistrationSec);
      } else {
        pushDetail(segments, 'MCC/MNC', data.mccMnc);
        pushDetail(segments, 'Version', data.version);
        pushDetail(segments, 'MPSS', data.mpssBuild);
        pushDetail(segments, 'LE Build', data.leBuild);
        pushDetail(segments, 'Free Flash', data.totalFreeAvailableFlashMemory != null ? formatBytes(data.totalFreeAvailableFlashMemory) : null);
        pushDetail(segments, 'Max Flash', data.totalMaxStorageFlashMemory != null ? formatBytes(data.totalMaxStorageFlashMemory) : null);
        if (data.mpssBuildUpdated) {
          segments.push({ text: '  ·  ' });
          segments.push({ text: 'MPSS updated from ' });
          segments.push({ code: data.previousMpssBuild });
        }
        if (data.leBuildUpdated) {
          segments.push({ text: '  ·  ' });
          segments.push({ text: 'LE updated from ' });
          segments.push({ code: data.previousLeBuild });
        }
      }
      break;
    }
    case 'TELEMETRY':
    default: {
      switch (event.eventSubType) {
        case 'THROUGHPUT':
          pushDetail(segments, 'UL Peak (Mbps)', data.ulPeakRateMbps);
          pushDetail(segments, 'DL Peak (Mbps)', data.dlPeakRateMbps);
          break;
        case 'MODEM_SSR':
          pushDetail(segments, 'Sub System', data.subSystem);
          pushDetail(segments, 'Event', data.event);
          pushDetail(segments, 'Reason', data.reason);
          break;
        case 'PDN':
          pushDetail(segments, 'Status', data.status);
          pushDetail(segments, 'Type', data.apnIpType);
          pushDetail(segments, 'Reason Type', data.reasonType);
          pushDetail(segments, 'Reason Code', data.reasonCode);
          pushDetail(segments, 'Mapped Reason', data.mappedReason);
          break;
        case 'NAS_REGISTRATION':
          pushDetail(segments, 'Status', data.status);
          pushDetail(segments, 'Reject Cause', data.rejectCause);
          break;
        case 'DATA_STALL':
          pushDetail(segments, 'Sub System', data.subsystem);
          pushDetail(segments, 'Recovery Action', data.recoveryAction);
          pushDetail(segments, 'Recovery Status', data.recoveryStatus);
          break;
        case 'BOOT_TIME':
          Object.entries(data.metrics || {}).forEach(([k, v]) => pushDetail(segments, k, `${v}s`));
          break;
        default:
          break;
      }
      break;
    }
  }
  return segments;
}

// Map one API unified-timeline event to the shape the timeline row renderer expects.
function mapTimelineEvent(event, index, referenceDate) {
  const { cat, dot } = categoryFor(event.eventType, event.isCritical);
  const { sub, subLabel } = subFilterFor(event);
  const badges = [];
  if (event.isCritical) badges.push({ t: 'Critical', c: 'crash' });
  badges.push(categoryBadge(event.eventType));
  return {
    id: `E${index}`,
    cat,
    sub,
    subLabel,
    dot,
    time: isoToClock(event.timestamp),
    date: isoToDate(event.timestamp),
    offset: offsetMinutes(event.timestamp, referenceDate),
    ts: new Date(event.timestamp).getTime(),
    name: event.eventTitle,
    severity: event.isCritical ? 'critical' : undefined,
    badges,
    detail: buildDetailSegments(event),
    // ODL / AD Log events carry a resolved list of context collection rules ({ id, label }); surfaced
    // via a per-event button + modal. Absent/empty for every other event type.
    contextCollectionRules: event.eventType === 'ODL' || event.eventType === 'AD_LOG'
      ? (event.data?.contextCollectionRules || []) : [],
    // ODL / AD Log merge id: the key the Client Debug service records for files it analysed.
    artifactId: logArtifactId(event),
  };
}

// Values must match the unified-timeline event's eventType exactly -- the
// download endpoint's `type` param is that eventType, not a separate token
// (req PDF 1.4.1: "&type=<CRASH | ODL> # matches the event's eventType").
const ARTIFACT_TYPE = { CRASH: 'CRASH', ODL_MERGED: 'ODL', AD_LOG_MERGED: 'AD_LOG' };

// Both artifact cards are populated from the same unified-timeline response
// (req PDF 1.3): filter=ALL/CRASHES surfaces CRASH events, filter=ODL surfaces
// ODL events. `crash-analysis` is a separate endpoint used only for the AI
// Crash Analysis reasoning card, not for artifact download listing.
function crashTimelineEvents(timelineEvents) {
  return (timelineEvents || []).filter(
    (e) => e && e.eventType === 'CRASH' && e.data && e.data.artifactId != null
  );
}

function crashArtifactFiles(events) {
  return events.map((e) => ({
    id: e.data.artifactId,
    type: ARTIFACT_TYPE.CRASH,
    name: e.data.filename,
    detail: e.data.dumpType || 'Crash dump',
    timestamp: e.timestamp || null,
  }));
}

function odlMergedEvents(timelineEvents) {
  return mergedLogEvents(timelineEvents, 'ODL');
}

// AD Log events share the ODL merged-file event shape (same odl_merge_jobs source).
function adLogMergedEvents(timelineEvents) {
  return mergedLogEvents(timelineEvents, 'AD_LOG');
}

function mergedLogEvents(timelineEvents, eventType) {
  return (timelineEvents || []).filter(
    (e) => e && e.eventType === eventType && e.data && e.data.artifactId != null
  );
}

function odlArtifactFiles(events) {
  return mergedArtifactFiles(events, ARTIFACT_TYPE.ODL_MERGED, 'Merged ODL package');
}

function adLogArtifactFiles(events) {
  return mergedArtifactFiles(events, ARTIFACT_TYPE.AD_LOG_MERGED, 'Merged AD log package');
}

function mergedArtifactFiles(events, type, fallbackName) {
  return events.map((e) => ({
    id: e.data.artifactId,
    type,
    name: e.data.filename || `${fallbackName} #${e.data.artifactId}`,
    detail: `${e.data.filesMergedCount ?? 0} files aggregated`,
    timestamp: e.timestamp || null,
  }));
}

function crashCardSubtitle(crashEvents, fileCount) {
  const dumpTypes = [];
  (crashEvents || []).forEach((e) => {
    if (e.data.dumpType && !dumpTypes.includes(e.data.dumpType)) {
      dumpTypes.push(e.data.dumpType);
    }
  });
  if (dumpTypes.length > 0) return dumpTypes.join(' • ');
  return `${fileCount} file${fileCount === 1 ? '' : 's'}`;
}

function buildArtifactCards({ timelineEvents } = {}) {
  const cards = [];
  const crashEvents = crashTimelineEvents(timelineEvents);
  if (crashEvents.length > 0) {
    cards.push({
      key: 'crash', type: ARTIFACT_TYPE.CRASH, tone: 'red', icon: '💥',
      title: 'Crash Dumps', count: String(crashEvents.length),
      sub: crashCardSubtitle(crashEvents, crashEvents.length), files: crashArtifactFiles(crashEvents),
    });
  }
  const odlEvents = odlMergedEvents(timelineEvents);
  if (odlEvents.length > 0) {
    const totalMerged = odlEvents.reduce((sum, e) => sum + (e.data.filesMergedCount || 0), 0);
    cards.push({
      key: 'odl', type: ARTIFACT_TYPE.ODL_MERGED, tone: 'blue', icon: '📋',
      title: 'ODL Packages', count: String(odlEvents.length),
      sub: `${totalMerged} files aggregated`, files: odlArtifactFiles(odlEvents),
    });
  }
  const adLogEvents = adLogMergedEvents(timelineEvents);
  if (adLogEvents.length > 0) {
    const totalMerged = adLogEvents.reduce((sum, e) => sum + (e.data.filesMergedCount || 0), 0);
    cards.push({
      key: 'ad-log', type: ARTIFACT_TYPE.AD_LOG_MERGED, tone: 'purple', icon: '🧭',
      title: 'AD Log Packages', count: String(adLogEvents.length),
      sub: `${totalMerged} files aggregated`, files: adLogArtifactFiles(adLogEvents),
    });
  }
  return cards;
}

function findArtifactCard(cards, key) {
  return (cards || []).find((c) => c.key === key) || null;
}

function artifactFileKey(file) {
  return `${file.type}:${file.id}`;
}

function describeDownloadFailure(err) {
  const status = err && err.status;
  if (status === 409) {
    return (err && err.reason) || 'This artifact is not available for download yet.';
  }
  if (status === 404) {
    return 'This artifact is no longer available.';
  }
  return 'Download failed. Please try again.';
}

// Stable color palette for the per-series dense line charts.
const DENSE_SERIES_COLORS = ['#3253DC', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'];

// Group dense-health rows into TimeSeriesChart `series` (one line per seriesKey value).
function buildDenseSeries(rows, seriesKey, valueKey) {
  if (!Array.isArray(rows)) return [];
  const bySeries = new Map();
  rows.forEach((row) => {
    const name = row[seriesKey] != null ? String(row[seriesKey]) : 'value';
    // bucket_start_epoch_sec may be numeric epoch seconds or an ISO string, depending on the endpoint.
    const x = parseBucketStart(row.bucket_start_epoch_sec);
    const y = Number(row[valueKey]);
    if (x === null || Number.isNaN(y)) return;
    if (!bySeries.has(name)) bySeries.set(name, []);
    bySeries.get(name).push({ x, y });
  });
  return Array.from(bySeries.entries()).map(([name, data], i) => ({
    name,
    color: DENSE_SERIES_COLORS[i % DENSE_SERIES_COLORS.length],
    data: data.sort((a, b) => a.x - b.x),
  }));
}

// Parse a bucket-start value into a Date. Accepts numeric epoch seconds or an ISO string. Returns null if unparseable.
function parseBucketStart(value) {
  if (value == null) return null;
  const num = Number(value);
  const d = Number.isNaN(num) ? new Date(value) : new Date(num * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Descriptor per dense line-chart chip: which health array + series/value keys + y-axis label.
const DENSE_LINE_CONFIG = {
  thermal: { arrayKey: 'thermal_trends', seriesKey: 'sensor_type', valueKey: 'max_metric_value_celsius', yAxisLabel: 'Temperature (°C)' },
  flash: { arrayKey: 'flash_usage', seriesKey: 'partition_name', valueKey: 'used_memory_mb', yAxisLabel: 'Memory (MB)' },
  memory: { arrayKey: 'memory_usage', seriesKey: 'sub_system', valueKey: 'used_memory_mb_max', yAxisLabel: 'Memory (MB)' },
  cpu: { arrayKey: 'cpu_usage', seriesKey: 'cpu_id', valueKey: 'usr_percent', yAxisLabel: 'Usage (%)' },
};

// Descriptor per dense histogram chip: which histogram array + axis labels.
const DENSE_HIST_CONFIG = {
  rsrp: { arrayKey: 'rsrpHistogram', xAxisLabel: 'RSRP (dBm)', color: '#3498db' },
  rsrq: { arrayKey: 'rsrqHistogram', xAxisLabel: 'RSRQ (dB)', color: '#e74c3c' },
  sinr: { arrayKey: 'sinrHistogram', xAxisLabel: 'SINR (dB)', color: '#2ecc71' },
};

// ── Client-side Activity Summary counts (moved off the backend) ──────────────
// "Crowdsourced Telemetry Data Points", "Crash Events", and "RT Telemetry Data
// Points" are no longer returned by /activity-summary — they are derived here
// from the already-fetched unified timeline (+ dense data), so the backend no
// longer runs the bulky counting queries. These numbers are intentionally
// timeline-scoped and will not match the old raw-DB counts.

// Count unified-timeline events of a given eventType.
function countEventsOfType(rawEvents, eventType) {
  if (!Array.isArray(rawEvents)) return 0;
  return rawEvents.reduce((n, e) => (e && e.eventType === eventType ? n + 1 : n), 0);
}

// "Crowdsourced Telemetry Data Points" = discrete TELEMETRY timeline events +
// every dense line-series point (thermal/flash/memory/cpu bucket rows) + every
// signal-histogram instance (rsrp/rsrq/sinr bin counts).
function countCrowdsourcedTelemetry(rawEvents, denseHealth, signalHistograms) {
  let total = countEventsOfType(rawEvents, 'TELEMETRY');
  if (denseHealth) {
    Object.values(DENSE_LINE_CONFIG).forEach((cfg) => {
      const rows = denseHealth[cfg.arrayKey];
      if (Array.isArray(rows)) total += rows.length;
    });
  }
  if (signalHistograms) {
    Object.values(DENSE_HIST_CONFIG).forEach((cfg) => {
      const bins = signalHistograms[cfg.arrayKey];
      if (Array.isArray(bins)) total += bins.reduce((n, b) => n + (Number(b.instances) || 0), 0);
    });
  }
  return total;
}

// Progress stages for the AI Timeline Summary — each maps to a real, observable
// milestone (not a fake timer), so the operator sees genuine forward motion.
const TIMELINE_SUMMARY_STAGES = {
  queued: { pct: 0, label: 'Queued — waiting for timeline data…' },
  preparing: { pct: 15, label: 'Preparing diagnostic data…' },
  analyzing: { pct: 50, label: 'Analyzing with AI…' },
};

// Reduce one pooled numeric series (points sorted by time) to a compact,
// anomaly-preserving stat block for the LLM. `points` = [{ t: ms, y: number }].
function reduceSeries(points, refEpoch) {
  if (!points.length) return { count: 0 };
  const sorted = [...points].sort((a, b) => a.t - b.t);
  const ys = sorted.map((p) => p.y);
  const n = ys.length;
  const iso = (ms) => toLocalIsoWithOffset(new Date(ms));
  const round = (v) => Math.round(v * 100) / 100;

  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const mean = ys.reduce((s, v) => s + v, 0) / n;
  const variance = ys.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  const stddev = Math.sqrt(variance);
  const p95 = [...ys].sort((a, b) => a - b)[Math.min(n - 1, Math.floor(0.95 * (n - 1)))];
  const first = ys[0];
  const last = ys[n - 1];

  // Peak point (with timestamp).
  let peak = sorted[0];
  for (const p of sorted) if (p.y > peak.y) peak = p;

  // Largest point-to-point jump (with timestamp of the later point).
  let largestJump = { delta: 0, t: sorted[0].t };
  for (let i = 1; i < n; i++) {
    const delta = sorted[i].y - sorted[i - 1].y;
    if (Math.abs(delta) > Math.abs(largestJump.delta)) largestJump = { delta, t: sorted[i].t };
  }

  // 8-bucket shape (mean per equal time bucket across the window).
  const shape = [];
  const t0 = sorted[0].t;
  const tN = sorted[n - 1].t;
  const span = tN - t0 || 1;
  const buckets = Array.from({ length: 8 }, () => []);
  for (const p of sorted) {
    const idx = Math.min(7, Math.floor(((p.t - t0) / span) * 8));
    buckets[idx].push(p.y);
  }
  buckets.forEach((b) => shape.push(b.length ? round(b.reduce((s, v) => s + v, 0) / b.length) : null));

  const out = {
    count: n,
    min: round(min), max: round(max), mean: round(mean), p95: round(p95), stddev: round(stddev),
    first: round(first), last: round(last), netChange: round(last - first),
    peak: { value: round(peak.y), ts: iso(peak.t) },
    largestJump: { delta: round(largestJump.delta), ts: iso(largestJump.t) },
    shape,
  };

  // Near-reference snapshot: point closest to the reference and the mean of the
  // reference's shape bucket.
  if (refEpoch != null && !Number.isNaN(refEpoch)) {
    let nearest = sorted[0];
    for (const p of sorted) if (Math.abs(p.t - refEpoch) < Math.abs(nearest.t - refEpoch)) nearest = p;
    const refBucketIdx = Math.min(7, Math.max(0, Math.floor(((refEpoch - t0) / span) * 8)));
    out.nearReference = {
      valueAtReference: round(nearest.y),
      valueTs: iso(nearest.t),
      referenceBucketMean: shape[refBucketIdx],
    };
  }

  return out;
}

// Reduce the dense-health response (thermal/flash/memory/cpu) to compact stats
// for the AI summary. Breaks each family out PER INSTANCE (per core / sensor /
// partition / subsystem) rather than pooling — mirroring how reduced telemetry
// groups are keyed "eventType/eventSubType" in dense_summary — so the LLM can
// compare individual instances (e.g. one saturated core, one hot sensor). Each
// instance becomes a "family/instance" key (e.g. "cpu/0", "thermal/cpuss-0").
// Skips signal histograms.
function reduceDenseHealth(dense, refEpoch) {
  if (!dense) return {};
  const summary = {};
  Object.entries(DENSE_LINE_CONFIG).forEach(([key, cfg]) => {
    const rows = dense[cfg.arrayKey];
    if (!Array.isArray(rows)) return;
    // Bucket points by their per-instance key (cfg.seriesKey) instead of pooling
    // the whole family into one series.
    const byInstance = new Map();
    rows.forEach((row) => {
      const d = parseBucketStart(row.bucket_start_epoch_sec);
      const y = Number(row[cfg.valueKey]);
      if (d === null || Number.isNaN(y)) return;
      const instance = row[cfg.seriesKey] != null ? String(row[cfg.seriesKey]) : 'value';
      if (!byInstance.has(instance)) byInstance.set(instance, []);
      byInstance.get(instance).push({ t: d.getTime(), y });
    });
    // Don't emit an empty stat block: the dense-health endpoint always returns
    // all 4 arrays (empty when there's no data), and rows can also be present
    // but all unparseable. Either way, a {count: 0} block would make
    // denseSummary non-empty and defeat the "no telemetry data" skip guard.
    byInstance.forEach((points, instance) => {
      if (points.length === 0) return;
      summary[`${key}/${instance}`] = reduceSeries(points, refEpoch);
    });
  });
  return summary;
}

function reduceSignalHistograms(histograms) {
  if (!histograms) return {};
  const summary = {};
  Object.entries(DENSE_HIST_CONFIG).forEach(([key, cfg]) => {
    const bins = histograms[cfg.arrayKey];
    if (!Array.isArray(bins) || bins.length === 0) return;
    let totalInstances = 0;
    let weightedSum = 0;
    let minVal = Infinity;
    let maxVal = -Infinity;
    let peakBin = null;
    let peakCount = 0;
    bins.forEach((b) => {
      const count = Number(b.instances) || 0;
      if (count === 0) return;
      const range = String(b.binInterval || '');
      const match = range.match(/(-?\d+(?:\.\d+)?)/);
      const val = match ? Number(match[1]) : null;
      totalInstances += count;
      if (val !== null) {
        weightedSum += val * count;
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
      }
      if (count > peakCount) { peakCount = count; peakBin = range; }
    });
    if (totalInstances === 0) return;
    summary[`signal_histogram_${key}`] = {
      _scope: 'full_day',
      metric: key.toUpperCase(),
      unit: cfg.xAxisLabel.replace(/.*\(/, '').replace(/\).*/, ''),
      totalSamples: totalInstances,
      min: minVal === Infinity ? null : minVal,
      max: maxVal === -Infinity ? null : maxVal,
      weightedMean: minVal === Infinity ? null : Math.round((weightedSum / totalInstances) * 100) / 100,
      peakBin,
      peakBinCount: peakCount,
      distribution: bins.filter((b) => (Number(b.instances) || 0) > 0).map((b) => ({ range: b.binInterval, count: Number(b.instances) })),
    };
  });
  return summary;
}

// Extract a compact, flat `params` object for a telemetry event, mirroring the
// curated fields buildDetailSegments renders in the UI. Only present values are
// included so the AI-summary payload stays small, and the polymorphic `data`
// blob is never forwarded wholesale. Scoped to the event types the diagnostic
// summary consumes (TELEMETRY, RT_TELEMETRY, AD_TELEMETRY); returns undefined when
// there is nothing useful to attach. Without these params two events of the same
// type/subtype are indistinguishable to the LLM apart from their timestamp.
function extractEventParams(event) {
  const data = event.data || {};
  const params = {};
  const set = (key, value) => {
    if (value === null || value === undefined || value === '') return;
    params[key] = value;
  };
  switch (event.eventType) {
    case 'AD_TELEMETRY': {
      // anomalyId is an identifier, not a measurement — sent as a string so the
      // volume reducer (numeric fields only) never turns it into a stat block.
      set('anomalyId', data.anomalyId != null ? String(data.anomalyId) : null);
      set('anomalyName', data.anomalyName);
      set('source', data.source);
      set('loggingState', data.loggingState);
      break;
    }
    case 'RT_TELEMETRY': {
      const payload = data.payload || {};
      const header = data.commonHeader || {};
      switch (event.eventSubType) {
        case 'signal-strength-rsrp':
        case 'signal-strength-rsrq':
        case 'signal-strength-sinr':
          set('lteValue', payload.lteValue);
          set('nr5gValue', payload.nr5gValue);
          break;
        case 'serving-cell-metrics':
          set('rat', header.wwanTech);
          set('pci', header.servingPci);
          set('earfcn', header.earfcn);
          set('arfcn', header.arfcn);
          if (header.mcc != null && header.mnc != null) set('mccMnc', `${header.mcc}/${header.mnc}`);
          set('lteValue', payload.lteValue);
          set('nr5gValue', payload.nr5gValue);
          break;
        case 'neighbor-cell-metrics':
          set('neighborCellCount', neighborCellCount(payload.neighborCellsJson));
          break;
        case 'throughput-performance':
          set('ulPeakMbps', bpsToMbps(payload.ulPeakBps));
          set('dlPeakMbps', bpsToMbps(payload.dlPeakBps));
          set('rttMs', payload.rttMs);
          set('jitterMs', payload.jitterMs);
          set('packetLossPct', payload.packetLossPct);
          break;
        default:
          break;
      }
      set('error', header.errorString);
      break;
    }
    case 'TELEMETRY':
    default: {
      switch (event.eventSubType) {
        case 'THROUGHPUT':
          set('ulPeakMbps', data.ulPeakRateMbps);
          set('dlPeakMbps', data.dlPeakRateMbps);
          break;
        case 'MODEM_SSR':
          set('subSystem', data.subSystem);
          set('event', data.event);
          set('reason', data.reason);
          break;
        case 'PDN':
          set('status', data.status);
          set('apnIpType', data.apnIpType);
          set('reasonType', data.reasonType);
          break;
        case 'NAS_REGISTRATION':
          set('status', data.status);
          set('rejectCause', data.rejectCause);
          break;
        case 'DATA_STALL':
          set('subsystem', data.subsystem);
          set('recoveryAction', data.recoveryAction);
          set('recoveryStatus', data.recoveryStatus);
          break;
        case 'BOOT_TIME':
          Object.entries(data.metrics || {}).forEach(([k, v]) => set(k, v));
          break;
        default:
          break;
      }
      break;
    }
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

// Annotate raw unified-timeline events with a signed offset_minutes relative to
// the reference, so the LLM doesn't have to do datetime arithmetic. Keeps the
// original event fields plus a curated flat `params` object (via
// extractEventParams) so the LLM can tell same-typed events apart by their
// measured values; the raw polymorphic `data` payload itself is not forwarded.
function annotateEvents(rawEvents, refEpoch) {
  if (!Array.isArray(rawEvents)) return [];
  const refDate = new Date(refEpoch);
  return rawEvents.map((e) => {
    const annotated = {
      timestamp: toLocalIsoWithOffset(e.timestamp),
      eventType: e.eventType,
      eventSubType: e.eventSubType,
      eventTitle: e.eventTitle,
      isCritical: e.isCritical,
      offset_minutes: offsetMinutes(e.timestamp, refDate),
    };
    const params = extractEventParams(e);
    if (params) annotated.params = params;
    return annotated;
  });
}

// Volume-adaptive reduction for the AI timeline payload. High-frequency sampled
// telemetry (THROUGHPUT, RT signal-strength, serving-cell, ...) can number in
// the thousands over a wide window and blow past the model's context limit,
// while the discrete state-change events an operator actually reads on a
// timeline (crash, modem SSR, data stall, NAS reject, PDN, boot) are few. Rather
// than hardcode which types are "dense", group by eventType/eventSubType at
// runtime and reduce any group larger than the threshold into per-field stat
// blocks (reusing reduceSeries), folding them into dense_summary. Smaller groups
// pass through raw. isCritical events, and events within NEAR_REFERENCE_MINUTES
// of the reference, are always kept raw regardless of group size so no anomaly
// and no fine detail near the investigation point is summarized away.
const EVENT_REDUCE_THRESHOLD = 50;
// Even inside a reduced (high-volume) group, keep raw any event within this many
// minutes of the reference so the model still gets fine-grained detail at the
// exact moment under investigation. These raw samples intentionally overlap the
// group's stat block (which always covers the FULL group as a complete
// envelope); the prompt explains the overlap so counts aren't double-read.
const NEAR_REFERENCE_MINUTES = 15;

function partitionAndReduceEvents(events, refEpoch) {
  const groups = new Map();
  for (const e of events) {
    const key = `${e.eventType}/${e.eventSubType}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  const keptEvents = [];
  const reduced = {};
  for (const [key, groupEvents] of groups) {
    if (groupEvents.length <= EVENT_REDUCE_THRESHOLD) {
      keptEvents.push(...groupEvents);
      continue;
    }
    // Large group: reduce each numeric params field to a stat block. Any
    // critical event is additionally kept raw so it still surfaces individually.
    const fieldPoints = {};
    for (const e of groupEvents) {
      const t = new Date(e.timestamp).getTime();
      if (Number.isNaN(t)) continue;
      const params = e.params || {};
      for (const [field, value] of Object.entries(params)) {
        if (typeof value !== 'number' || Number.isNaN(value)) continue;
        if (!fieldPoints[field]) fieldPoints[field] = [];
        fieldPoints[field].push({ t, y: value });
      }
    }
    const block = { count: groupEvents.length };
    for (const [field, points] of Object.entries(fieldPoints)) {
      block[field] = reduceSeries(points, refEpoch);
    }
    reduced[key] = block;
    // Additionally keep raw any critical event, or any event within
    // NEAR_REFERENCE_MINUTES of the reference, so individual detail survives
    // where the operator is actually looking. The stat block still spans the
    // whole group; these raw events are a superset-detail overlay, not a
    // separate population.
    for (const e of groupEvents) {
      if (e.isCritical || Math.abs(e.offset_minutes) <= NEAR_REFERENCE_MINUTES) keptEvents.push(e);
    }
  }
  // Keep the raw events chronological for the timeline narrative.
  keptEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  return { keptEvents, reduced };
}

// Render a timeline detail line built from { text } / { code } segments.
function DetailLine({ segments }) {
  return (
    <div className="dp-tl-detail">
      {segments.map((seg, i) =>
        seg.code ? <code key={i}>{seg.code}</code> : <span key={i}>{seg.text}</span>
      )}
    </div>
  );
}

// Context Window select options → half-width of the window in minutes.
const CONTEXT_WINDOW_OPTIONS = [
  ['± 5 minutes', 5],
  ['± 15 minutes', 15],
  ['± 30 minutes', 30],
  ['± 1 hour', 60],
  ['± 3 hours', 180],
  ['± 6 hours', 360],
  ['± 12 hours', 720],
  ['± 24 hours', 1440],
  ['± 2 days', 2 * 1440],
  ['± 3 days', 3 * 1440],
  ['± 4 days', 4 * 1440],
  ['± 5 days', 5 * 1440],
  ['± 6 days', 6 * 1440],
  ['± 7 days', 7 * 1440],
];

// Format a Date as an ISO UTC date-time string (no timezone suffix), e.g. "2025-07-10T09:00:00".
function toIsoLocal(date) {
  const p = (n) => n.toString().padStart(2, '0');
  return (
    `${date.getUTCFullYear()}-${p(date.getUTCMonth() + 1)}-${p(date.getUTCDate())}` +
    `T${p(date.getUTCHours())}:${p(date.getUTCMinutes())}:${p(date.getUTCSeconds())}`
  );
}

// Parse a reference timestamp (datetime-local value "YYYY-MM-DDTHH:mm:ss", entered
// in the browser's local time zone) into a Date. No offset suffix => parsed as local.
function parseReferenceTimestamp(value) {
  if (!value) return new Date(NaN);
  return new Date(value);
}

// Per data-source behavior for the Investigate run. Keyed by the Data Sources
// dropdown value. `runCrash`/`runTimeline`/`runDense` gate the respective API
// calls; `timelineFilter` is the unified-timeline `filter` query param;
// `activityFilter` is the activity-summary `filter` query param (same token names,
// plus RD_SESSION which the timeline has no equivalent for) — the backend returns
// the non-matching fields already set to "No Data", so the UI just renders them.
// The context sections (Current Session State, Device Profile, Status Banner,
// Health Score) are intentionally not gated — they always fetch and render.
const DATA_SOURCE_CONFIG = {
  'All Sources': { runCrash: true, activityFilter: 'ALL', timelineFilter: 'ALL', runTimeline: true, runDense: true },
  'Crashes': { runCrash: true, activityFilter: 'CRASHES', timelineFilter: 'CRASHES', runTimeline: true, runDense: false },
  'Telemetry': { runCrash: false, activityFilter: 'TELEMETRY', timelineFilter: 'TELEMETRY', runTimeline: true, runDense: true },
  'Logs': { runCrash: false, activityFilter: 'ODL', timelineFilter: 'ODL', runTimeline: true, runDense: false },
  'RD Session': { runCrash: false, activityFilter: 'RD_SESSION', timelineFilter: null, runTimeline: false, runDense: false },
  // Combined RT source: one dropdown option driving BOTH RT Activity Summary rows.
  // "RT Telemetry Data Points" is computed client-side from the RT_TELEMETRY timeline
  // (so runTimeline + timelineFilter='RT_TELEMETRY'); "RT Sessions" is the backend count,
  // which the 'RT' activity-summary token keeps populated.
  'RT': { runCrash: false, activityFilter: 'RT', timelineFilter: 'RT_TELEMETRY', runTimeline: true, runDense: false },
  'Health Score': { runCrash: false, activityFilter: 'HEALTH', timelineFilter: 'HEALTH', runTimeline: true, runDense: false },
  // AD (Anomaly Detection) sources. Both are timeline-only; "Anomalies (Device Detected)"
  // is computed client-side from the AD_TELEMETRY timeline. The backend activity summary
  // has no AD fields, so these tokens leave every backend field at "No Data".
  'AD Logs': { runCrash: false, activityFilter: 'AD_LOGS', timelineFilter: 'AD_LOGS', runTimeline: true, runDense: false },
  'AD Telemetry': { runCrash: false, activityFilter: 'AD_TELEMETRY', timelineFilter: 'AD_TELEMETRY', runTimeline: true, runDense: false },
};

function DeviceProfile({ initialSystemId, initialReferenceTimestamp, initialContextWindow, autoInvestigate = false, userInfo, role, setNdStartDevice, setSelectedTab }) {
  // Investigation form (visual only for now — no fetch)
  const [systemId, setSystemId] = useState(initialSystemId || '');

  // When pre-mount pattern passes a new systemId after initial render, update the field
  React.useEffect(() => {
    if (initialSystemId) {
      setSystemId(initialSystemId);
      setHasInvestigated(false);
      setCrashAnalysis(null);
      setCrashError(null);
    }
  }, [initialSystemId]);

  const [refTimestamp, setRefTimestamp] = useState(() => initialReferenceTimestamp || (() => {
    const now = new Date();
    return now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0') + 'T' +
      String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0') + ':' +
      String(now.getSeconds()).padStart(2, '0');
  })());
  const [contextWindow, setContextWindow] = useState(initialContextWindow || '± 1 hour');
  const [dataSource, setDataSource] = useState('All Sources');

  // Whether the user has clicked "Investigate" yet — gates all result sections below
  const [hasInvestigated, setHasInvestigated] = useState(false);

  // Snapshot of systemId at the time Investigate was last clicked — used for display only.
  const [investigatedSystemId, setInvestigatedSystemId] = useState('');
  const [investigatedRefTimestamp, setInvestigatedRefTimestamp] = useState('');
  // Snapshot of the Data Sources selection at Investigate time — gates which of the
  // client-computed Activity Summary fields (crowdsourced/crash/RT telemetry) apply.
  const [investigatedDataSource, setInvestigatedDataSource] = useState('All Sources');

  // Per-device active-session state — drives enforcement in DebugStrategies and the
  // Active Debug Sessions list. Tracks the currently investigated device.
  const activeDeviceId = investigatedSystemId || systemId;
  const { rdSessions, rtSessions, hasActiveRd, hasActiveRt, refresh: refreshActiveSessions } =
    useDeviceActiveSessions(activeDeviceId, userInfo, role);

  // Live Crash Analysis data (fetched on Investigate)
  const [crashAnalysis, setCrashAnalysis] = useState(null);
  const [crashLoading, setCrashLoading] = useState(false);
  const [crashError, setCrashError] = useState(null);

  // Live Device Profile / Activity Summary data (fetched on Investigate)
  const [profileState, setProfileState] = useState({ data: null, loading: false, error: null, notFound: false });
  const [activityState, setActivityState] = useState({ data: null, loading: false, error: null });

  // Health Score API (owned by the Health Score team) — derives the Thermal
  // status row and the Anomalies (Cloud Detected) override; degrades independently.
  const [healthScoreState, setHealthScoreState] = useState({ data: null, loading: false, error: null });

  // Live Unified Timeline (fetched on Investigate)
  const [timelineEventsLive, setTimelineEventsLive] = useState([]);
  // Raw (unmapped) timeline events -- Artifact Quick Access needs the typed
  // `data` payload (e.g. ODL merge artifactId) that mapTimelineEvent discards.
  const [timelineEventsRaw, setTimelineEventsRaw] = useState([]);
  // ODL / AD logs of the window that Client Debug analysed: { [artifactId]: analyses[] }. Decoration only;
  // a request counter drops answers that arrive after a newer investigation has started.
  const [analyzedBySource, setAnalyzedBySource] = useState({});
  const analyzedReqRef = useRef(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // AI Timeline Summary (generated on Investigate from the already-fetched
  // timeline + dense data — the expensive endpoint is never re-invoked).
  const [timelineSummary, setTimelineSummary] = useState({ data: null, loading: false, error: null, progress: 0, stage: null });

  // Live Dense Data (fetched on Investigate) -- drives the chip -> chart modals.
  const [denseHealth, setDenseHealth] = useState(null);
  const [signalHistograms, setSignalHistograms] = useState(null);
  const [denseError, setDenseError] = useState(null);
  // Crowdsourced Telemetry Data Points is computed from timeline + dense data, so its
  // Activity Summary pill stays in "Loading…" until both those fetches settle.
  const [denseLoading, setDenseLoading] = useState(false);

  // Investigation window (ISO local strings) -- pins the dense line-chart x-axis.
  const [investigationWindow, setInvestigationWindow] = useState(null);
  const pendingAutoInvestigation = useRef(null);

  // Compute the +/- window around the reference timestamp and fetch all live sections.
  const runInvestigation = async ({ contextWindow: requestedContextWindow } = {}) => {
    setHasInvestigated(true);
    setInvestigatedSystemId(systemId);
    setInvestigatedRefTimestamp(refTimestamp);
    setInvestigatedDataSource(dataSource);

    const sourceConfig = DATA_SOURCE_CONFIG[dataSource] || DATA_SOURCE_CONFIG['All Sources'];

    const ref = parseReferenceTimestamp(refTimestamp);
    if (Number.isNaN(ref.getTime())) {
      setCrashError('Invalid reference timestamp. Please select a valid date and time.');
      setCrashAnalysis(null);
      return;
    }
    const selectedContextWindow = requestedContextWindow || contextWindow;
    const halfWindowMin =
      (CONTEXT_WINDOW_OPTIONS.find(([label]) => label === selectedContextWindow) || ['', 60])[1];
    const startTimestamp = toIsoLocal(new Date(ref.getTime() - halfWindowMin * 60000));
    const endTimestamp = toIsoLocal(new Date(ref.getTime() + halfWindowMin * 60000));
    const referenceTimestamp = toIsoLocal(ref);
    // Current Session State is live-right-now state, not tied to the investigation window.
    const nowTimestamp = toIsoLocal(new Date());

    setInvestigationWindow({ startTimestamp, endTimestamp, referenceTimestamp });
    
    setCrashLoading(true);
    setCrashError(null);
    setProfileState((s) => ({ ...s, loading: true, error: null, notFound: false }));
    setActivityState((s) => ({ ...s, loading: true, error: null }));
    setHealthScoreState((s) => ({ ...s, loading: true, error: null }));
    setTimelineLoading(true);
    setDenseLoading(true);
    setTimelineError(null);
    const analyzedReq = ++analyzedReqRef.current;
    setAnalyzedBySource({});
    setTimelineSummary({
      data: null,
      loading: sourceConfig.runTimeline,
      error: null,
      progress: 0,
      stage: sourceConfig.runTimeline ? 'queued' : null,
    });

    // Timeline + dense fetches are hoisted so the AI-summary branch can reuse
    // their raw results — this guarantees the expensive unified-timeline
    // endpoint is invoked exactly once (here), never again for the summary.
    const timelinePromise = (async () => {
      if (!sourceConfig.runTimeline) {
        setTimelineEventsLive([]);
        setTimelineEventsRaw([]);
        setTimelineLoading(false);
        return null;
      }
      try {
        const data = await fetchDeviceProfileUnifiedTimeline({
          systemId, startTimestamp, endTimestamp, filter: sourceConfig.timelineFilter,
        });
        const events = data.events || [];
        const mapped = events.map((e, i) => mapTimelineEvent(e, i, ref));
        setTimelineEventsLive(mapped);
        setTimelineEventsRaw(events);
        // Not awaited: the badges appear when the lookup answers, the timeline never waits for it.
        const logIds = collectLogSourceIds(events);
        if (logIds.length > 0) {
          fetchAnalyzedLogs({ systemId, sourceIds: logIds }).then((analyzed) => {
            if (analyzedReq === analyzedReqRef.current) setAnalyzedBySource(indexAnalyzedLogs(analyzed));
          });
        }
        return events;
      } catch (err) {
        setTimelineError(`Failed to load timeline: ${err.message}`);
        setTimelineEventsLive([]);
        setTimelineEventsRaw([]);
        return null;
      } finally {
        setTimelineLoading(false);
      }
    })();

    const densePromise = (async () => {
      setDenseError(null);
      if (!sourceConfig.runDense) {
        setDenseHealth(null);
        setSignalHistograms(null);
        return { health: null, histograms: null };
      }
      try {
        const [health, histograms] = await Promise.all([
          fetchDeviceProfileDenseHealth({ systemId, startTimestamp, endTimestamp }),
          fetchDeviceProfileSignalHistograms({ systemId, startTimestamp, endTimestamp }),
        ]);
        setDenseHealth(health);
        setSignalHistograms(histograms);
        return { health, histograms };
      } catch (err) {
        setDenseError(`Failed to load dense data: ${err.message}`);
        setDenseHealth(null);
        setSignalHistograms(null);
        return { health: null, histograms: null };
      }
    })();

    await Promise.allSettled([
      (async () => {
        if (!sourceConfig.runCrash) {
          setCrashAnalysis({ processedFiles: [] });
          setCrashLoading(false);
          return;
        }
        try {
          const data = await fetchDeviceProfileCrashAnalysis({ systemId, startTimestamp, endTimestamp });
          setCrashAnalysis(data);
        } catch (err) {
          setCrashError(`Failed to load crash analysis: ${err.message}`);
          setCrashAnalysis(null);
        } finally {
          setCrashLoading(false);
        }
      })(),
      (async () => {
        try {
          const data = await fetchDeviceProfile({ systemId, referenceTimestamp });
          setProfileState({ data, loading: false, error: null, notFound: false });
        } catch (err) {
          setProfileState({ data: null, loading: false, error: err.message, notFound: err.status === 404 });
        }
      })(),
      (async () => {
        try {
          const data = await fetchActivitySummary({ systemId, startTimestamp, endTimestamp, filter: sourceConfig.activityFilter });
          setActivityState({ data, loading: false, error: null });
        } catch (err) {
          setActivityState({ data: null, loading: false, error: err.message });
        }
      })(),
      (async () => {
        try {
          const data = await fetchHealthScore({ systemId, startTimestamp, endTimestamp });
          setHealthScoreState({ data, loading: false, error: null });
        } catch (err) {
          setHealthScoreState({ data: null, loading: false, error: err.message });
        }
      })(),
      timelinePromise,
      densePromise,
      // Crowdsourced Telemetry Data Points is derived from both the timeline and the
      // dense data — clear its loading flag only once both have settled.
      (async () => {
        await Promise.allSettled([timelinePromise, densePromise]);
        setDenseLoading(false);
      })(),
      (async () => {
        if (!sourceConfig.runTimeline) {
          setTimelineSummary({ data: null, loading: false, error: null, progress: 0, stage: null });
          return;
        }
        // Reuse the already-fetched timeline events + dense health; never re-fetch.
        const [rawEvents, denseResult] = await Promise.all([timelinePromise, densePromise]);
        const health = denseResult?.health ?? null;
        const histograms = denseResult?.histograms ?? null;
        if (rawEvents === null) {
          setTimelineSummary({ data: null, loading: false, error: 'Timeline unavailable — summary skipped.', progress: 0, stage: null });
          return;
        }
        try {
          // 15% — dependencies resolved; preparing the payload (reduce/annotate).
          setTimelineSummary({ data: null, loading: true, error: null, progress: 15, stage: 'preparing' });
          const refEpoch = ref.getTime();
          const annotated = annotateEvents(rawEvents, refEpoch)
            // Diagnostic AI summary consumes telemetry data only (regular + RT + AD telemetry).
            .filter((e) => e.eventType === 'TELEMETRY' || e.eventType === 'RT_TELEMETRY' || e.eventType === 'AD_TELEMETRY');
          // Volume-adaptively reduce high-frequency sampled telemetry into stat
          // blocks so the payload stays within the model's context limit; keep
          // low-volume and critical events raw.
          const { keptEvents: events, reduced: reducedTelemetry } = partitionAndReduceEvents(annotated, refEpoch);
          const denseSummary = { ...reduceDenseHealth(health, refEpoch), ...reducedTelemetry, ...reduceSignalHistograms(histograms) };
          // Nothing to analyze — no regular/RT/AD telemetry events and no dense health
          // data in the window. Skip the LLM call (and the token fetch) entirely and
          // show a no-data notice instead.
          if (events.length === 0 && Object.keys(denseSummary).length === 0) {
            setTimelineSummary({ data: null, loading: false, error: null, progress: 0, stage: null, empty: true });
            return;
          }
          const token = await fetch(`${process.env.REACT_APP_WBC_API_BASE_URL}/api/v1/apigee/token`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d?.access_token || null)
            .catch(() => null);
          // 50% — payload sent to the LLM; awaiting the response.
          setTimelineSummary({ data: null, loading: true, error: null, progress: 50, stage: 'analyzing' });
          const result = await fetchDeviceProfileTimelineSummary({
            token,
            deviceId: systemId,
            window: {
              start: toLocalIsoWithOffset(new Date(ref.getTime() - halfWindowMin * 60000)),
              end: toLocalIsoWithOffset(new Date(ref.getTime() + halfWindowMin * 60000)),
            },
            referenceTimestamp: toLocalIsoWithOffset(ref),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            events,
            denseSummary,
          });
          if (result?.errorMessage) {
            setTimelineSummary({ data: null, loading: false, error: result.errorMessage, progress: 0, stage: null });
          } else {
            setTimelineSummary({ data: result?.insights || null, loading: false, error: null, progress: 100, stage: 'done' });
          }
        } catch (err) {
          setTimelineSummary({ data: null, loading: false, error: err.message, progress: 0, stage: null });
        }
      })(),
    ]);
    refreshActiveSessions();
  };

  // Analytics event deep-links should land on the Device subtab with the
  // selected system already investigated. State is staged first so the
  // investigation uses the incoming timestamp and range-derived context.
  React.useEffect(() => {
    if (!autoInvestigate || !initialSystemId) return;
    setSystemId(initialSystemId);
    if (initialReferenceTimestamp) setRefTimestamp(initialReferenceTimestamp);
    if (initialContextWindow) setContextWindow(initialContextWindow);
    setHasInvestigated(false);
    setInvestigatedSystemId('');
    setCrashAnalysis(null);
    setCrashError(null);
    pendingAutoInvestigation.current = {
      systemId: initialSystemId,
      referenceTimestamp: initialReferenceTimestamp || null,
      contextWindow: initialContextWindow,
    };
  }, [autoInvestigate, initialSystemId, initialReferenceTimestamp, initialContextWindow]);

  React.useEffect(() => {
    const pendingLaunch = pendingAutoInvestigation.current;
    if (!autoInvestigate || !pendingLaunch
      || pendingLaunch.systemId !== systemId
      || (pendingLaunch.referenceTimestamp && pendingLaunch.referenceTimestamp !== refTimestamp)) return undefined;
    pendingAutoInvestigation.current = null;
    const timer = setTimeout(() => runInvestigation({ contextWindow: pendingLaunch.contextWindow }), 0);
    return () => clearTimeout(timer);
    // runInvestigation intentionally uses the staged state from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoInvestigate, systemId, refTimestamp, contextWindow, initialContextWindow]);

  // Timeline filter: selected category keys; empty means "All Events".
  const [tlFilters, setTlFilters] = useState([]);
  // Event Type sub-filter: { [categoryKey]: [subKey, ...] }; a missing/empty entry means "All"
  // sub-types of that category.
  const [tlSubFilters, setTlSubFilters] = useState({});
  const clearTlSubFilter = (cat) => {
    setTlSubFilters((prev) => {
      const next = { ...prev };
      delete next[cat];
      return next;
    });
  };
  const toggleTlFilter = (key) => {
    if (key === 'all') {
      setTlFilters([]);
      setTlSubFilters({});
      return;
    }
    if (tlFilters.includes(key)) clearTlSubFilter(key);
    setTlFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const toggleTlSubFilter = (cat, sub) => {
    setTlSubFilters((prev) => {
      const current = prev[cat] || [];
      const next = current.includes(sub) ? current.filter((s) => s !== sub) : [...current, sub];
      return { ...prev, [cat]: next };
    });
  };

  // Single modal descriptor: { type: 'artifact'|'chart', key } | { type: 'contextRules', rules }
  const [modal, setModal] = useState(null);
  const closeModal = () => setModal(null);

  // Per-file download state, keyed by artifactFileKey(file): { status: 'loading'|'error', message }
  const [downloadStatus, setDownloadStatus] = useState({});

  const artifactCards = buildArtifactCards({ timelineEvents: timelineEventsRaw });

  const downloadArtifact = async (file) => {
    const key = artifactFileKey(file);
    setDownloadStatus((prev) => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const { url } = await fetchArtifactDownloadUrl({
        systemId: investigatedSystemId,
        type: file.type,
        id: file.id,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
      setDownloadStatus((prev) => ({ ...prev, [key]: { status: 'idle' } }));
    } catch (err) {
      setDownloadStatus((prev) => ({ ...prev, [key]: { status: 'error', message: describeDownloadFailure(err) } }));
    }
  };

  const visibleFilters = timelineFilters;
  // One Event Type sub-filter group per selected category (or, with "All Events", per category
  // present in the fetched events), listing the distinct sub-types present (with counts).
  // Categories with fewer than two sub-types get no group.
  const subFilterCats = tlFilters.length > 0
    ? tlFilters
    : visibleFilters.map((f) => f.key).filter((k) => timelineEventsLive.some((e) => e.cat === k));
  const subFilterGroups = subFilterCats
    .map((cat) => {
      const subs = new Map();
      timelineEventsLive.forEach((e) => {
        if (e.cat !== cat) return;
        const entry = subs.get(e.sub) || { key: e.sub, label: e.subLabel, count: 0 };
        entry.count += 1;
        subs.set(e.sub, entry);
      });
      const filter = visibleFilters.find((f) => f.key === cat);
      return { cat, label: filter ? filter.label : cat, c: filter ? filter.c : cat, subs: [...subs.values()] };
    })
    .filter((g) => g.subs.length > 1);
  // Only categories with a rendered sub-filter group apply their selection, so a stale selection
  // (e.g. after re-investigating) can't silently hide events with no visible control to clear it.
  const subFilteredCats = new Set(subFilterGroups.map((g) => g.cat));
  const visibleEvents = timelineEventsLive.filter((e) => {
    if (tlFilters.length > 0 && !tlFilters.includes(e.cat)) return false;
    const subs = subFilteredCats.has(e.cat) ? tlSubFilters[e.cat] : null;
    return !subs || subs.length === 0 || subs.includes(e.sub);
  });
  // ODL / AD logs among the visible events that were sent to an analysis.
  const analysesOf = (e) => (e.artifactId ? analyzedBySource[e.artifactId] : null);
  const analyzedLogCount = visibleEvents.filter((e) => analysesOf(e)).length;
  // The reference marker follows the last event at or before the reference (offset <= 0).
  const referenceAfterId = (() => {
    const before = visibleEvents.filter((e) => e.offset <= 0);
    return before.length > 0 ? before[before.length - 1].id : null;
  })();

  // Health-score-derived override for the Activity Summary "Anomalies (Cloud
  // Detected)" pill; null (backend's own "Unavailable") if the call hasn't succeeded.
  const cloudAnomalyCount = countBadPenalties(healthScoreState.data);

  // ── Activity Summary: merge backend fields with the 4 client-computed ones ──
  // "Crowdsourced Telemetry Data Points", "Anomalies (Device Detected)", "Crash
  // Events", and "RT Telemetry Data Points" are not sent by the backend — they are
  // derived here from the already-fetched unified timeline (+ dense data) and
  // re-inserted in their original display order. This `derivedActivity` list is the
  // single source of truth for both the Activity Summary section and the Status
  // Banner below.
  //
  // Applicability mirrors the fetch plan: a computed field is only meaningful when
  // its source was actually fetched for the investigated Data Source. When it
  // wasn't (e.g. "Logs" doesn't run the timeline for telemetry), the field reports
  // "No Data" — matching the backend's prior per-filter behavior — rather than a
  // misleading 0.
  const derivedActivity = (() => {
    const backendFields = activityState.data || [];
    const sourceConfig = DATA_SOURCE_CONFIG[investigatedDataSource] || DATA_SOURCE_CONFIG['All Sources'];
    // Which computed fields apply for this Data Source. Crowdsourced/crash come from
    // whether the timeline (+ dense) telemetry/crash sources ran; RT telemetry from
    // the RT_TELEMETRY timeline filter; device-detected anomalies from AD_TELEMETRY.
    const crowdsourcedApplies = sourceConfig.activityFilter === 'ALL' || sourceConfig.activityFilter === 'TELEMETRY';
    const crashApplies = sourceConfig.activityFilter === 'ALL' || sourceConfig.activityFilter === 'CRASHES';
    const rtTelemetryApplies = sourceConfig.activityFilter === 'ALL' || sourceConfig.activityFilter === 'RT_TELEMETRY'
      || sourceConfig.activityFilter === 'RT';
    const deviceAnomaliesApplies = sourceConfig.activityFilter === 'ALL' || sourceConfig.activityFilter === 'AD_TELEMETRY';

    const crowdsourcedValue = crowdsourcedApplies
      ? String(countCrowdsourcedTelemetry(timelineEventsRaw, denseHealth, signalHistograms))
      : 'No Data';
    const crashValue = crashApplies ? String(countEventsOfType(timelineEventsRaw, 'CRASH')) : 'No Data';
    const rtTelemetryValue = rtTelemetryApplies ? String(countEventsOfType(timelineEventsRaw, 'RT_TELEMETRY')) : 'No Data';
    const deviceAnomaliesValue = deviceAnomaliesApplies
      ? String(countEventsOfType(timelineEventsRaw, 'AD_TELEMETRY'))
      : 'No Data';

    const get = (name) => backendFields.find((f) => f.fieldName === name)?.value ?? 'No Data';
    // Rebuild the original 8-field order the UI used before these 4 moved client-side.
    return [
      { fieldName: 'Crowdsourced Telemetry Data Points', value: crowdsourcedValue },
      { fieldName: 'Anomalies (Device Detected)', value: deviceAnomaliesValue },
      { fieldName: 'Anomalies (Cloud Detected)', value: get('Anomalies (Cloud Detected)') },
      { fieldName: 'RD Sessions', value: get('RD Sessions') },
      { fieldName: 'Crash Events', value: crashValue },
      { fieldName: 'ODL Uploads', value: get('ODL Uploads') },
      { fieldName: 'RT Telemetry Data Points', value: rtTelemetryValue },
      { fieldName: 'RT Sessions', value: get('RT Sessions') },
    ];
  })();

  // The 4 client-computed fields depend on timeline + dense fetches, so their pills
  // stay in "Loading…" until those settle (not just the activity-summary fetch).
  const COMPUTED_ACTIVITY_FIELDS = new Set([
    'Crowdsourced Telemetry Data Points', 'Anomalies (Device Detected)', 'Crash Events', 'RT Telemetry Data Points',
  ]);
  const activityFieldLoading = (fieldName) =>
    COMPUTED_ACTIVITY_FIELDS.has(fieldName)
      ? (timelineLoading || denseLoading)
      : activityState.loading;

  // ── Modal content ──────────────────────────────────────────────────────
  const renderModalInner = () => {
    if (!modal) return null;

    if (modal.type === 'artifact') {
      const card = findArtifactCard(artifactCards, modal.key);
      const files = card ? card.files : [];
      return (
        <>
          <div className="modal-header">
            <h2>{card ? card.title : modal.key}</h2>
            <button className="modal-close-button" onClick={closeModal}>✕</button>
          </div>
          <div className="modal-body">
            {files.map((f) => {
              const key = artifactFileKey(f);
              const status = downloadStatus[key] || {};
              return (
                <div className="dp-modal-file-row" key={key}>
                  <div>
                    <div className="dp-modal-file-name">{f.name}</div>
                    <div className="dp-modal-file-sub">
                      {f.detail}{f.timestamp ? ` • Collected: ${isoToDateTime(f.timestamp)}` : ''}
                    </div>
                    {status.status === 'error' && <div className="dp-modal-file-error">{status.message}</div>}
                  </div>
                  <button
                    className="dp-modal-download"
                    disabled={status.status === 'loading'}
                    onClick={() => downloadArtifact(f)}
                  >
                    {status.status === 'loading' ? 'Downloading…' : '⬇ Download'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      );
    }

    if (modal.type === 'chart') {
      const chip = denseTelemetryChips.find((c) => c.key === modal.key);
      const label = chip ? chip.label : modal.key;
      const lineConfig = DENSE_LINE_CONFIG[modal.key];
      const histConfig = DENSE_HIST_CONFIG[modal.key];

      let title;
      let body;
      if (lineConfig) {
        title = `${label} — Timeseries`;
        const series = buildDenseSeries(
          denseHealth ? denseHealth[lineConfig.arrayKey] : [],
          lineConfig.seriesKey,
          lineConfig.valueKey
        );
        const timeRange = investigationWindow
          ? { type: 'custom', from: investigationWindow.startTimestamp, to: investigationWindow.endTimestamp }
          : '24h';
        body = <TimeSeriesChart title={label} series={series} yAxisLabel={lineConfig.yAxisLabel} timeRange={timeRange} referenceTimestamp={investigationWindow?.referenceTimestamp} timeZone="local" />;
      } else if (histConfig) {
        title = `${label} — Distribution`;
        const bins = ((signalHistograms && signalHistograms[histConfig.arrayKey]) || []).map((b) => ({
          range: b.binInterval,
          count: b.instances,
        }));
        body = (
          <HistogramChart
            title={label}
            subtitle="Distribution over the full day(s) of the selected window — not limited to the ± context range"
            bins={bins}
            xAxisLabel={histConfig.xAxisLabel}
            yAxisLabel="Number of Instances"
            color={histConfig.color}
          />
        );
      } else {
        title = label;
        body = <div className="dp-ai-summary">No chart configured for {label}.</div>;
      }

      return (
        <>
          <div className="modal-header">
            <h2>{title}</h2>
            <button className="modal-close-button" onClick={closeModal}>✕</button>
          </div>
          <div className="modal-body">
            <div className="dp-chart-modal-body">
              {denseError && <div className="dp-ai-error">{denseError}</div>}
              {body}
            </div>
          </div>
        </>
      );
    }

    if (modal.type === 'contextRules') {
      const rules = modal.rules || [];
      return (
        <>
          <div className="modal-header">
            <h2>Context Collection Rules</h2>
            <button className="modal-close-button" onClick={closeModal}>✕</button>
          </div>
          <div className="modal-body">
            {rules.map((r) => (
              <div className="dp-modal-file-row" key={r.id}>
                <div>
                  <div className="dp-modal-file-name">{r.label || `Rule #${r.id}`}</div>
                  <div className="dp-modal-file-sub">ID: {r.id}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    return null;
  };

  // ── Device profile card ──────────────────────────────────────────────
  // `card.rows` is the API's flat [{fieldName, value}] list for one group;
  // styling (muted/tag) is derived by field name rather than hardcoded.
  const renderProfileCard = (card) => (
    <div className="dp-card" key={card.title}>
      <div className="dp-pc-title">{card.title}</div>
      {card.rows.map((row) => {
        const r = { ...row, value: localizedProfileValue(row) };
        const style = styleForField(r.fieldName, r.value);
        return (
          <div className="dp-pc-row" key={r.fieldName}>
            <span className="dp-pk">{r.fieldName}</span>
            <span className={`dp-pv${style.muted ? ' muted' : ''}`}>
              {style.tag === 'ok' ? (
                <span className="dp-tag-ok">{r.value}</span>
              ) : style.tag === 'changed' ? (
                r.params?.freeFlash ? (
                  <span className="dp-tag-info-wrap">
                    <span className="dp-tag-changed">{r.value}</span>
                    <span className="dp-info-icon" data-tooltip={`Free: ${r.params.freeFlash} / Max: ${r.params.maxFlash}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                    </span>
                  </span>
                ) : (
                  <span className="dp-tag-changed">{r.value}</span>
                )
              ) : (
                r.value
              )}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="content-tab-container">
      <div className="device-profile dp-root">
        {/* ── START INVESTIGATION ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon blue">🔍</div>
          <h2>Start Investigation</h2>
          <div className="dp-sec-line" />
        </div>
        <div className="dp-inv-card">
          <div className="dp-inv-row">
            <div className="dp-inv-field wide">
              <label>System ID</label>
              <input type="text" value={systemId} onChange={(e) => setSystemId(e.target.value)} placeholder="e.g. QC-DEV-XXXX-00000" />
            </div>
            <div className="dp-inv-field med">
              <label>Reference Timestamp</label>
              <input type="datetime-local" step="1" value={refTimestamp} onChange={(e) => setRefTimestamp(e.target.value)} />
            </div>
            <div className="dp-inv-field sm">
              <label>Context Window</label>
              <select value={contextWindow} onChange={(e) => setContextWindow(e.target.value)}>
                {CONTEXT_WINDOW_OPTIONS.map(([label]) => (
                  <option key={label}>{label}</option>
                ))}
              </select>
            </div>
            <div className="dp-inv-field sm">
              <label>Data Sources</label>
              <select value={dataSource} onChange={(e) => setDataSource(e.target.value)}>
                <option>All Sources</option>
                <option>Telemetry</option>
                <option>Crashes</option>
                <option>RD Session</option>
                <option>Logs</option>
                <option>RT</option>
                <option>Health Score</option>
                <option>AD Logs</option>
                <option>AD Telemetry</option>
              </select>
            </div>
            <button className="dp-btn-primary" onClick={runInvestigation} disabled={!systemId.trim()}>⚡ Investigate</button>
            <button
              className="dp-btn-secondary"
              onClick={() => {
                setHasInvestigated(false);
                setInvestigatedSystemId('');
                setInvestigatedRefTimestamp('');
                setInvestigatedDataSource('All Sources');
                setSystemId('');
                setRefTimestamp(deviceSummary.referenceTimestamp);
                setContextWindow('± 1 hour');
                setDataSource('All Sources');
                setCrashAnalysis(null);
                setCrashError(null);
                setCrashLoading(false);
                setProfileState({ data: null, loading: false, error: null, notFound: false });
                setActivityState({ data: null, loading: false, error: null });
                setHealthScoreState({ data: null, loading: false, error: null });
                setTimelineEventsLive([]);
                setTimelineEventsRaw([]);
                analyzedReqRef.current += 1;
                setAnalyzedBySource({});
                setTimelineError(null);
                setTimelineLoading(false);
                setDenseHealth(null);
                setSignalHistograms(null);
                setDenseError(null);
                setDenseLoading(false);
                setInvestigationWindow(null);
              }}
            >
              Clear
            </button>
          </div>
        </div>

        {hasInvestigated && (
        <>
        {/* ── DEBUG STRATEGIES (RD Quick-Start) ── */}
        <div className="dp-sec-hdr" style={{ marginTop: 28 }}>
          <div className="dp-sec-icon green">⚡</div>
          <h2>Debug Strategies</h2>
          <span className="dp-sec-sub">
            One-click RD session launch for this device — no config authoring needed
          </span>
          <div className="dp-sec-line" />
        </div>
        <DebugStrategies
          systemId={investigatedSystemId || systemId}
          userInfo={userInfo}
          hasActiveRd={hasActiveRd}
          hasActiveRt={hasActiveRt}
          refresh={refreshActiveSessions}
          setNdStartDevice={setNdStartDevice}
          setSelectedTab={setSelectedTab}
        />
        <ActiveDebugSessions
          rdSessions={rdSessions}
          rtSessions={rtSessions}
          onViewRd={() => { if (setNdStartDevice) setNdStartDevice({ systemId: (investigatedSystemId || systemId).trim(), target: 'rd', l2: 'view' }); if (setSelectedTab) setSelectedTab('New Diagnostics'); }}
          onViewRt={() => { if (setNdStartDevice) setNdStartDevice({ systemId: (investigatedSystemId || systemId).trim(), target: 'rt', l2: 'view' }); if (setSelectedTab) setSelectedTab('New Diagnostics'); }}
          refresh={refreshActiveSessions}
        />

        {/* ── STATUS BANNER ── */}
        <div className="dp-stats-banner">
          <div className="dp-sb-cell device-cell">
            <div className="dp-device-id">{investigatedSystemId}</div>
            <div className="dp-device-meta">
              {profileState.loading ? (
                'Loading…'
              ) : (() => {
                const identity = profileState.data?.IDENTITY || [];
                const get = (name) => identity.find((f) => f.fieldName === name)?.value || '—';
                return <>Make: {get('Make')} &bull; OEM: {get('OEM')} &bull; Model: {get('Model')}</>;
              })()}
            </div>
          </div>
          {(() => {
            const activity = derivedActivity;
            const getActivity = (name) => activity.find((f) => f.fieldName === name)?.value ?? '—';
            const crashEvents = getActivity('Crash Events');
            const deviceAnomalies = getActivity('Anomalies (Device Detected)');
            const cloudAnomalies = cloudAnomalyCount != null ? String(cloudAnomalyCount) : getActivity('Anomalies (Cloud Detected)');

            // Data Points = sum of EVERY numeric Activity Summary value (data
            // points + crash events + anomalies + RD/RT sessions + ODL uploads).
            // Non-numeric values ("Unavailable" / "No Data" / "—") are skipped.
            // The Cloud-Detected count uses the Health Score override when present,
            // matching what the Activity Summary section renders. Returns null
            // (→ "—") when no field carries a number.
            const dataPointsTotal = (() => {
              let total = 0;
              let sawNumber = false;
              activity.forEach((f) => {
                const raw = f.fieldName === 'Anomalies (Cloud Detected)' && cloudAnomalyCount != null
                  ? cloudAnomalyCount
                  : f.value;
                const n = Number(raw);
                if (Number.isFinite(n) && String(raw).trim() !== '') { total += n; sawNumber = true; }
              });
              return sawNumber ? total : null;
            })();
            const dataPoints = dataPointsTotal != null ? dataPointsTotal.toLocaleString() : '—';

            const healthWindows = healthScoreState.data?.healthScores || [];
            // Prefer the most recent window in range; if the range has no windows,
            // fall back to the preceding window (last score before the range), then
            // the following window.
            const latestHealth =
              healthWindows.length > 0
                ? healthWindows[healthWindows.length - 1]
                : healthScoreState.data?.precedingHealthScore || healthScoreState.data?.followingHealthScore || null;
            const healthValue = latestHealth ? `${latestHealth.scorePercentage} / 100` : '—';
            // windowStart is a naive UTC string (no 'Z'); append 'Z' so it parses
            // as UTC, then render in local time to match the rest of the tab.
            const healthSub = latestHealth
              ? `Calculated At: ${isoToDateTime(latestHealth.windowStart + 'Z', { withSeconds: false })}`
              : null;

            // Crash Events is now client-computed from the timeline, and Data Points
            // sums the computed fields too — both wait on timeline + dense, not just
            // the activity-summary fetch.
            const computedLoading = timelineLoading || denseLoading;
            // Each cell shows a loading placeholder (never stale data from a
            // previous Investigate) until its source section has resolved.
            const cells = [
              { label: 'CRASH EVENTS', value: crashEvents, tone: crashEvents !== '0' && crashEvents !== '—' ? 'red' : 'default', sub: null, loading: computedLoading },
              { label: 'ANOMALIES (DEVICE DETECTED)', value: deviceAnomalies, tone: deviceAnomalies !== '0' && deviceAnomalies !== '—' && deviceAnomalies !== 'No Data' ? 'warning' : 'default', sub: null, loading: computedLoading },
              { label: 'ANOMALIES (CLOUD DETECTED)', value: cloudAnomalies, tone: cloudAnomalies !== '0' && cloudAnomalies !== '—' ? 'danger' : 'default', sub: null, loading: activityState.loading || healthScoreState.loading },
              { label: 'HEALTH SCORE', value: healthValue, tone: latestHealth == null ? 'default' : latestHealth.scorePercentage >= 75 ? 'success' : latestHealth.scorePercentage >= 50 ? 'warning' : 'danger', sub: healthSub, loading: healthScoreState.loading },
              { label: 'DATA POINTS', value: dataPoints, tone: 'purple', sub: null, loading: activityState.loading || computedLoading },
            ];
            return cells.map((c) => (
              <div className="dp-sb-cell" key={c.label}>
                <div className="dp-sb-lbl">{c.label}</div>
                {c.loading ? (
                  <div className="dp-sb-val dp-sb-loading">Loading…</div>
                ) : (
                  <div className={`dp-sb-val dp-val-${c.tone}`}>{c.value}</div>
                )}
                {!c.loading && c.sub && <div className="dp-sb-sub">{c.sub}</div>}
              </div>
            ));
          })()}
        </div>

        {/* ── DEVICE PROFILE ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon green">📱</div>
          <h2>Device Profile</h2>
          <span className="dp-sec-sub">
            State reconstructed at reference time — {investigatedRefTimestamp ? isoToDateTime(investigatedRefTimestamp) : ''}
          </span>
          <div className="dp-sec-line" />
        </div>
        {profileState.loading && <div className="dp-card">Loading device profile…</div>}
        {!profileState.loading && profileState.notFound && (
          <div className="dp-card">No profile data found at or before this reference time.</div>
        )}
        {!profileState.loading && profileState.error && !profileState.notFound && (
          <div className="dp-card dp-inv-error">Failed to load device profile: {profileState.error}</div>
        )}
        {!profileState.loading && profileState.data && (
          <div className="dp-profile-grid">
            {PROFILE_GROUPS.map((g) => {
              const rows = profileState.data[g.key] || [];
              // Thermal isn't part of the device-profile contract — it's derived
              // client-side from the Health Score API and appended to Device State.
              const cardRows =
                g.key === 'Device State'
                  ? [...rows, { fieldName: 'Thermal', value: resolveThermalStatus(healthScoreState.data) ?? 'No Data' }]
                  : rows;
              return renderProfileCard({ title: g.title, rows: cardRows });
            })}
          </div>
        )}

        {/* ── ACTIVITY SUMMARY ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon green">🔄</div>
          <h2>Activity Summary</h2>
          <span className="dp-sec-sub">Aggregated activity across the full selected time window</span>
          <div className="dp-sec-line" />
        </div>
        <div className="dp-card">
          {activityState.loading && <div className="dp-activity-pill">Loading activity summary…</div>}
          {!activityState.loading && activityState.error && (
            <div className="dp-activity-pill dp-inv-error">Failed to load activity summary: {activityState.error}</div>
          )}
          {!activityState.loading && !activityState.error &&
            derivedActivity.map((s) => {
              const meta = ACTIVITY_FIELD_META[s.fieldName] || { icon: '•', tone: 'muted' };
              // Backend always sends "Unavailable" for this field — override with the
              // Health Score API's bad-penalty count when that call has succeeded.
              const value =
                s.fieldName === 'Anomalies (Cloud Detected)' && cloudAnomalyCount != null
                  ? String(cloudAnomalyCount)
                  : s.value;
              // Client-computed fields (crowdsourced/crash/RT telemetry) depend on the
              // timeline + dense fetches — show a per-pill placeholder until they settle.
              const loading = activityFieldLoading(s.fieldName);
              return (
                <div className="dp-activity-pill" key={s.fieldName}>
                  <span className="dp-ap-label">{meta.icon} {s.fieldName}</span>
                  <span className={`dp-ap-count ${loading ? 'muted' : meta.tone}`}>
                    {loading ? 'Loading…' : value}
                  </span>
                </div>
              );
            })}
        </div>

        {/* ── AI-ASSISTED REASONING ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon purple">🤖</div>
          <h2>AI-Assisted Reasoning</h2>
          <div className="dp-sec-line" />
        </div>

        {/* Analysis — live AI Timeline Summary generated on Investigate from the
            already-fetched timeline + reduced dense health (the expensive
            unified-timeline endpoint is not re-invoked). */}
        <div className={`dp-ai-card${timelineSummary.data?.severity ? ' ' + timelineSummary.data.severity : ''}`}>
          <div className="dp-ai-header">
            <div className="dp-ai-label">
              <div className="dp-ai-icon">{aiReasoning.analysis.icon}</div>
              <div>
                <div className="dp-ai-label-text">{aiReasoning.analysis.title}</div>
              </div>
            </div>
            {timelineSummary.data && (
              <div className="dp-confidence-badge">
                {timelineSummary.data.confidence}% confidence
                <span className="dp-info-tip">
                  <svg className="dp-info-tip-icon" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span className="dp-info-bubble">
                    Confidence reflects how well-grounded this AI summary is, based on the volume and
                    completeness of telemetry in the selected window. Abundant, consistent data scores
                    90–100; sparse or missing data scores below 40.
                  </span>
                </span>
              </div>
            )}
          </div>
          {timelineSummary.loading && (
            <div className="dp-ai-progress">
              <div className="dp-ai-progress-head">
                <span className="dp-ai-progress-stage">
                  {(TIMELINE_SUMMARY_STAGES[timelineSummary.stage] || {}).label || 'Generating summary…'}
                </span>
                <span className="dp-ai-progress-pct">{timelineSummary.progress}%</span>
              </div>
              <div className="dp-ai-progress-track">
                <div
                  className={`dp-ai-progress-fill${timelineSummary.stage === 'analyzing' ? ' pulsing' : ''}`}
                  style={{ width: `${timelineSummary.progress}%` }}
                />
              </div>
            </div>
          )}
          {!timelineSummary.loading && timelineSummary.error && (
            <div className="dp-ai-error">⚠ Summary failed: {timelineSummary.error}</div>
          )}
          {!timelineSummary.loading && !timelineSummary.error && timelineSummary.empty && (
            <div className="dp-ai-summary">No telemetry data found for this device in the selected time window.</div>
          )}
          {!timelineSummary.loading && !timelineSummary.error && timelineSummary.data && (
            <div className="dp-ai-summary">
              <div>{timelineSummary.data.overall_summary}</div>
              {timelineSummary.data.key_findings?.length > 0 && (
                <ul>
                  {timelineSummary.data.key_findings.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
              {timelineSummary.data.patterns?.length > 0 && (
                <>
                  <div className="dp-ai-label-text" style={{ marginTop: 16 }}>Insights &amp; Patterns</div>
                  <ul>
                    {timelineSummary.data.patterns.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </>
              )}
              <div className="dp-ai-label-text" style={{ marginTop: 16 }}>Around Reference Timestamp</div>
              <div style={{ marginTop: 8 }}>{timelineSummary.data.reference_window_summary}</div>
              {timelineSummary.data.reference_events?.length > 0 && (
                <ul>
                  {timelineSummary.data.reference_events.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {signalHistograms && (signalHistograms.rsrpHistogram?.length > 0 || signalHistograms.rsrqHistogram?.length > 0 || signalHistograms.sinrHistogram?.length > 0) && (
                <div className="dp-ai-footnote" style={{ marginTop: 16, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                  Note: SINR, RSRP, and RSRQ insights reflect the distribution over the full day(s) of the selected window — not limited to the ± context range around the reference timestamp.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Crash Analysis */}
        <div className="dp-ai-card">
          <div className="dp-ai-header">
            <div className="dp-ai-label">
              <div className="dp-ai-icon">{aiReasoning.crash.icon}</div>
              <div>
                <div className="dp-ai-label-text">{aiReasoning.crash.title}</div>
              </div>
            </div>
          </div>
          <div className="dp-ai-summary">
            {crashLoading && <div>Loading crash analysis…</div>}
            {!crashLoading && crashError && <div className="dp-ai-error">{crashError}</div>}
            {!crashLoading && !crashError && crashAnalysis &&
              (crashAnalysis.processedFiles && crashAnalysis.processedFiles.length > 0 ? (
                crashAnalysis.processedFiles.map((f, idx) => (
                  <div key={`${f.filename}-${idx}`}>
                    {idx > 0 && <hr className="dp-ai-divider" />}
                    <div className="dp-ai-file-name"><code>{f.filename}</code></div>
                    {f.dumpType && <div className="dp-ai-kv"><span className="k">Dump Type</span><span className="v">{f.dumpType}</span></div>}
                    <div className="dp-ai-kv"><span className="k">Crash Area</span><span className="v">{f.problem?.crashArea}</span></div>
                    <div className="dp-ai-kv"><span className="k">Crash Error</span><span className="v">{f.problem?.crashError}</span></div>
                    <div className="dp-ai-kv"><span className="k">Error Signature</span><span className="v">{f.problem?.errorSignature}</span></div>
                    <div className="dp-ai-kv"><span className="k">Software PL</span><span className="v">{f.problem?.softwarePl}</span></div>
                    <div className="dp-ai-kv"><span className="k">Mitigation Recommendation</span><span className="v">{f.problem?.mitigationRecommendation}</span></div>
                    {(f.cr_matched_data || []).map((cr) => (
                      <div key={cr.changeRequestId}>
                        <hr className="dp-ai-divider" />
                        <div className="dp-ai-kv"><span className="k">Fixed Build</span><span className="v">{cr.fixedBuild}</span></div>
                        <div className="dp-ai-kv"><span className="k">CR ID</span><span className="v">{cr.changeRequestId}</span></div>
                        <div className="dp-ai-kv"><span className="k">CR Title</span><span className="v">{cr.changeRequestTitle}</span></div>
                        <div className="dp-ai-kv"><span className="k">Matched Image</span><span className="v">{cr.matchedImage}</span></div>
                        <div className="dp-ai-kv"><span className="k">Matched Product</span><span className="v">{cr.matchedProduct}</span></div>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div>No crash analysis found for this device in the selected time window.</div>
              ))}
          </div>
        </div>

        {/* Log Analysis — all ODL/AD logs in the window + every analysis run, regardless of timeline filter */}
        {(() => {
          const logEvts = timelineEventsLive.filter((e) => e.artifactId);
          const analyzedCount = logEvts.filter((e) => analyzedBySource[e.artifactId]).length;
          const badgeText = logEvts.length === 0
            ? 'No logs'
            : `${analyzedCount}/${logEvts.length} log${logEvts.length === 1 ? '' : 's'} analysed`;
          return (
            <div className="dp-ai-card">
              <div className="dp-ai-header">
                <div className="dp-ai-label">
                  <div className="dp-ai-icon">📄</div>
                  <div><div className="dp-ai-label-text">Debug Log Analysis</div></div>
                </div>
                <div className="dp-confidence-badge">{badgeText}</div>
              </div>
              {logEvts.length === 0 ? (
                <div className="dp-ai-summary">No ODL or AD logs in this time window.</div>
              ) : (
                <div className="dp-log-analysis-list">
                  {logEvts.map((e) => {
                    const analyses = analyzedBySource[e.artifactId];
                    return (
                      <div key={e.id} className="dp-log-analysis-row">
                        <div className="dp-log-analysis-meta">
                          <span className={`dp-tl-badge dp-tb-${e.dot}`}>
                            {e.cat === 'adlog' ? 'AD' : 'ODL'}
                          </span>
                          <span className="dp-log-analysis-time">{e.date} {e.time}</span>
                          <span className="dp-log-analysis-name">{e.name}</span>
                          {analyses
                            ? <span className="dp-tl-badge dp-tb-analysed">Analysed</span>
                            : <span className="dp-tl-badge dp-tb-pending">Not Analysed</span>}
                          {analyses && analyses.some((a) => a.emailSent) && (
                            <span className="dp-tl-badge dp-tb-email">Email Sent</span>
                          )}
                        </div>
                        {analyses && (
                          <div className="dp-log-analysis-details">
                            {analyses.map((a) => (
                              <div key={`${a.jobId}-${a.analysisId}`}>🔬 {describeAnalysis(a)}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── UNIFIED EVENT TIMELINE ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon orange">⏱️</div>
          <h2>Unified Event Timeline</h2>
          <div className="dp-sec-line" />
        </div>

        <div className="dp-timeline-wrapper">
          <div className="dp-timeline-toolbar">
            <span className="dp-tl-label">Filter:</span>
            {visibleFilters.map((f) => (
              <span
                key={f.key}
                className={`dp-filter-pill dp-fp-${f.c}${(f.key === 'all' ? tlFilters.length === 0 : tlFilters.includes(f.key)) ? ' active' : ''}`}
                onClick={() => toggleTlFilter(f.key)}
              >
                {f.label}
              </span>
            ))}
            <span className="dp-tl-count">
              {analyzedLogCount > 0 && (
                <span className="dp-tl-analysed-count">
                  🔬 {analyzedLogCount} analysed log{analyzedLogCount === 1 ? '' : 's'}
                </span>
              )}
              {visibleEvents.length} event{visibleEvents.length === 1 ? '' : 's'}
            </span>
          </div>

          {subFilterGroups.length > 0 && (
            <div className="dp-timeline-subtoolbar">
              <span className="dp-tl-label">Event Type:</span>
              {subFilterGroups.map((g) => {
                const selected = tlSubFilters[g.cat] || [];
                return (
                  <span key={g.cat} className="dp-subfilter-group">
                    <span className="dp-subfilter-group-label">{g.label}:</span>
                    <span
                      className={`dp-filter-pill dp-subfilter-pill dp-fp-${g.c}${selected.length === 0 ? ' active' : ''}`}
                      onClick={() => clearTlSubFilter(g.cat)}
                    >
                      All
                    </span>
                    {g.subs.map((s) => (
                      <span
                        key={s.key}
                        className={`dp-filter-pill dp-subfilter-pill dp-fp-${g.c}${selected.includes(s.key) ? ' active' : ''}`}
                        onClick={() => toggleTlSubFilter(g.cat, s.key)}
                      >
                        {s.label} ({s.count})
                      </span>
                    ))}
                  </span>
                );
              })}
            </div>
          )}

          <div className="dp-dense-row">
            <span className="dp-tl-label">Dense Telemetry Data:</span>
            {denseTelemetryChips.map((c) => (
              <span key={c.key} className="dp-ts-chip" onClick={() => setModal({ type: 'chart', key: c.key })}>
                {c.icon} {c.label}
              </span>
            ))}
          </div>

          <div className="dp-timeline-body">
            {timelineLoading && <div className="dp-ai-summary">Loading timeline…</div>}
            {!timelineLoading && timelineError && <div className="dp-ai-error">{timelineError}</div>}
            {!timelineLoading && !timelineError && visibleEvents.length === 0 && (
              <div className="dp-ai-summary">No events found for this device in the selected time window.</div>
            )}
            {/* Reference marker at the top when all events are after the reference time */}
            {!timelineLoading && !timelineError && referenceAfterId === null && visibleEvents.length > 0 && (
              <div className="dp-epoch">
                <div className="dp-epoch-line" />
                <div className="dp-epoch-label">🔴 Reference Event: {refTimestamp ? isoToDateTime(refTimestamp) : ''}</div>
                <div className="dp-epoch-line" />
              </div>
            )}
            {!timelineLoading && !timelineError && visibleEvents.map((e) => (
              <React.Fragment key={e.id}>
                <div className="dp-tl-group">
                  <div className={`dp-tl-entry${e.severity ? ' ' + e.severity : ''}`}>
                    <div className="dp-tl-time-col">
                      <div className="dp-tl-time">{e.time}</div>
                      <div className="dp-tl-date">{e.date}</div>
                      <div className={`dp-tl-offset ${e.offset < 0 ? 'before' : 'after'}`}>
                        {e.offset < 0 ? '−' : '+'}{Math.abs(e.offset)}m
                      </div>
                    </div>
                    <div className="dp-tl-line-col">
                      <div className={`dp-tl-dot dp-dot-${e.dot}`} />
                      <div className="dp-tl-connector" />
                    </div>
                    <div className="dp-tl-content">
                      <div className="dp-tl-head">
                        <span className="dp-tl-name">{e.name}</span>
                        {e.badges.map((b, i) => (
                          <span key={i} className={`dp-tl-badge dp-tb-${b.c}`}>{b.t}</span>
                        ))}
                        {analysesOf(e) && <span className="dp-tl-badge dp-tb-analysed">Analysed</span>}
                      </div>
                      {e.detail && e.detail.length > 0 && <DetailLine segments={e.detail} />}
                      {analysesOf(e) && (
                        <div className="dp-tl-analysed">
                          {analysesOf(e).slice(0, 3).map((a) => (
                            <div key={`${a.jobId}-${a.analysisId}`}>🔬 {describeAnalysis(a)}</div>
                          ))}
                          {analysesOf(e).length > 3 && <div>+{analysesOf(e).length - 3} more</div>}
                        </div>
                      )}
                      {e.contextCollectionRules && e.contextCollectionRules.length > 0 && (
                        <div className="dp-tl-links">
                          <button
                            className="dp-tl-link"
                            onClick={() => setModal({ type: 'contextRules', rules: e.contextCollectionRules })}
                          >
                            📋 Context Collection Rules ({e.contextCollectionRules.length})
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <hr className="dp-tl-divider" />
                </div>

                {/* Reference-event marker, inserted after its anchor event */}
                {e.id === referenceAfterId && (
                  <div className="dp-epoch">
                    <div className="dp-epoch-line" />
                    <div className="dp-epoch-label">🔴 Reference Event: {refTimestamp ? isoToDateTime(refTimestamp) : ''}</div>
                    <div className="dp-epoch-line" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── SUB SYSTEM ANOMALIES ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon orange">⚠️</div>
          <h2>Sub System Anomalies</h2>
          <span className="dp-sec-sub">Illustrative canned data — not live</span>
          <div className="dp-sec-line" />
        </div>
        <div className="dp-submod-grid">
          {subModuleAnomalies.map((m) => (
            <div className="dp-submod-card" key={m.key}>
              <div className="dp-submod-header">
                <div className={`dp-submod-icon ${m.iconClass}`}>{m.icon}</div>
                <div className="dp-submod-title">
                  <div className="dp-submod-name">{m.name}</div>
                  <div className="dp-submod-subtitle">{m.subtitle}</div>
                </div>
                <span className={`dp-submod-badge dp-badge-${m.badge.c}`}>{m.badge.text}</span>
              </div>
              <div className="dp-submod-body">
                {m.rows.map((r, i) => (
                  <div className="dp-submod-row" key={i}>
                    <div className={`dp-anomaly-dot ${r.dot}`} />
                    <div className="dp-anomaly-text">
                      <div className="dp-anomaly-label">{r.label}</div>
                      <div className="dp-anomaly-detail">{r.detail}</div>
                    </div>
                    <div className="dp-anomaly-time">{r.time}</div>
                  </div>
                ))}
              </div>
              <div className="dp-submod-footer">
                <span className="dp-submod-link">{m.footer}</span>
                <span className="dp-submod-count">{m.rows.length} anomalies</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── ARTIFACT QUICK ACCESS ── */}
        <div className="dp-sec-hdr">
          <div className="dp-sec-icon blue">🔗</div>
          <h2>Artifact Quick Access</h2>
          <span className="dp-sec-sub">Crash dumps, merged ODL packages and merged AD log packages available for download</span>
          <div className="dp-sec-line" />
        </div>
        {timelineLoading ? (
          <div className="dp-ai-summary">Loading artifacts…</div>
        ) : artifactCards.length > 0 ? (
          <div className="dp-artifact-grid">
            {artifactCards.map((a) => (
              <div className={`dp-artifact-card ${a.tone}`} key={a.key} onClick={() => setModal({ type: 'artifact', key: a.key })}>
                <div className="dp-ac-icon">{a.icon}</div>
                <div className="dp-ac-title">{a.title}</div>
                <div className="dp-ac-count">{a.count}</div>
                <div className="dp-ac-sub">{a.sub}</div>
                <div className="dp-ac-action">→ View Files</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="dp-ai-summary">No artifacts found for this device in the selected time window.</div>
        )}
        </>
        )}

        {/* ── MODAL ── */}
        {modal && (
          <div
            className="modal-overlay device-profile dp-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          >
            <div
              className={`modal-content${modal.type === 'chart' ? ' timeline-modal' : ''}`}
              style={modal.type === 'chart' ? { width: '90%', maxWidth: 720 } : { width: 520 }}
            >
              {renderModalInner()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceProfile;
